const DEFAULTS = {
  modalTarget: "swatch-modal",
  template: "swatch-modal",
};

const SECTION_SELECTOR = '[id^="shopify-section-"]';

/**
 * Wraps a button that loads the swatch modal markup from an alternate product
 * template and hands it to the `swatch-modal` markup-modal.
 *
 * <swatch-request-trigger product-url="/products/jasper-duvet-cover">
 *   <button type="button">Request a Swatch</button>
 * </swatch-request-trigger>
 */
class SwatchRequestTrigger extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector("button");
    if (!this.button) return;

    // Warm the request on intent so the drawer opens without a visible wait.
    this.handlePrefetch = () => this._fetch();
    this.handleClick = () => this._open();

    // The markup carries the bag's swatches — pre-selected, and spent against
    // the per-order limit — so a cart change makes the cached copy stale.
    this.handleCartUpdate = () => {
      this.request = null;
    };

    this.button.addEventListener("mouseenter", this.handlePrefetch);
    this.button.addEventListener("focus", this.handlePrefetch);
    this.button.addEventListener("click", this.handleClick);
    document.addEventListener("updateCart", this.handleCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener("updateCart", this.handleCartUpdate);

    if (!this.button) return;

    this.button.removeEventListener("mouseenter", this.handlePrefetch);
    this.button.removeEventListener("focus", this.handlePrefetch);
    this.button.removeEventListener("click", this.handleClick);
  }

  _fetch() {
    if (!this.productUrl) return Promise.resolve(null);

    if (!this.request) {
      const url = `${this.productUrl}?view=${this.template}`;

      this.request = fetch(url)
        .then((response) => response.text())
        .then((response) => {
          const parsed = new DOMParser().parseFromString(response, "text/html");

          return parsed.querySelector(SECTION_SELECTOR)?.innerHTML || null;
        })
        .catch(() => {
          console.warn(`Error fetching data from ${url}`);
          return null;
        });
    }

    return this.request;
  }

  async _open() {
    if (this.loading) return;

    this.loading = true;
    this.button.setAttribute("aria-busy", "true");

    let html;

    try {
      html = await this._fetch();
    } finally {
      this.loading = false;
      this.button.removeAttribute("aria-busy");
    }

    // Retry on the next click if the fetch failed or the section was missing.
    if (typeof html !== "string" || html.trim() === "") {
      this.request = null;
      return;
    }

    this.button.dispatchEvent(
      new CustomEvent("markup-modal:open", {
        bubbles: true,
        detail: {
          target: this.modalTarget,
          html,
        },
      }),
    );
  }

  get productUrl() {
    return this.getAttribute("product-url");
  }

  get template() {
    return this.getAttribute("template") || DEFAULTS.template;
  }

  get modalTarget() {
    return this.getAttribute("modal-target") || DEFAULTS.modalTarget;
  }
}

if (!customElements.get("swatch-request-trigger")) {
  customElements.define("swatch-request-trigger", SwatchRequestTrigger);
}
